import {
  Box,
  Button,
  Dialog,
  Typography,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Divider,
} from '@material-ui/core';
import { Add, Delete, Visibility, VisibilityOff } from '@material-ui/icons';
import React, { useCallback, useEffect, useRef } from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useFirestoreConnect } from 'react-redux-firebase';
import PageLayout from '../components/organisms/PageLayout';
import { Doc, FilesOptions, useMedia } from '../hooks';
import { RootState } from '../store/reducers';

const Upload: React.FC = () => {
  const inputRef = useRef<any>();
  const categories = useSelector((state: RootState) => state.firestore?.ordered?.categories ?? []);
  const docs: Doc[] = useSelector((state: RootState) => state.firestore?.ordered?.media ?? []);
  const { uploadMedias, loading, loaded, createCategory } = useMedia();
  const [files, setFiles] = useState<File[]>();
  const [open, setOpen] = useState(false);
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [categoryLabel, setCategoryLabel] = useState('');
  const [filesOptions, setFilesOptions] = useState<FilesOptions>({});

  const sendFile = useCallback(async () => {
    if (files) {
      await uploadMedias({ path: '/documents', files, filesOptions });
    }
  }, [files, filesOptions, uploadMedias]);

  const addCategory = useCallback(async () => {
    if (categoryLabel) {
      await createCategory(categoryLabel);
      setCategoryLabel('');
      setOpen(false);
    }
  }, [categoryLabel, createCategory]);

  useEffect(() => {
    if (loaded && inputRef && inputRef.current) {
      (inputRef as any).current.value = '';
    }
  }, [loaded]);

  useEffect(() => {
    setDuplicates(
      Array.from(files ?? [])?.filter((file) => docs?.find((doc) => doc.metadata.name === file.name)) ?? []
    );
  }, [docs, files]);

  useEffect(() => {
    setFilesOptions({});
  }, [files]);

  useFirestoreConnect({ collection: 'categories' });
  useFirestoreConnect({ collection: 'media' });

  return (
    <PageLayout>
      <Box paddingY={2}>
        <Paper variant="outlined">
          <Box padding={2}>
            <Box paddingBottom={2}>
              {loading && <LinearProgress />}
              {loaded && <small>File uploaded</small>}
            </Box>
            <Box paddingBottom={1} display="flex" justifyContent="space-between">
              <Button
                variant="contained"
                size="small"
                color="primary"
                disableElevation
                onClick={() => inputRef.current.click()}
              >
                Seleziona files
              </Button>

              <Button
                variant="contained"
                size="small"
                color="primary"
                disableElevation
                onClick={sendFile}
                disabled={
                  loading ||
                  !files?.length ||
                  !!duplicates.length ||
                  Object.keys(filesOptions).length !== files?.length ||
                  Object.values(filesOptions).some((option) => !option.category)
                }
              >
                Carica
              </Button>
            </Box>
            <Box display="none">
              <input type="file" onChange={(e: any) => setFiles(e.target.files)} ref={inputRef} multiple />
            </Box>
            <Box paddingY={3}>
              {files &&
                !!Array.from(files).length &&
                Array.from(files).map((file) => (
                  <Box paddingY={1} display="flex" alignItems="center" key={file.name}>
                    <Box width="100%">
                      {file.name}{' '}
                      {duplicates.includes(file) && (
                        <Box>
                          <Typography color="error" variant="caption">
                            Un file con questo nome esiste già, prova a rinominare il file e ricaricarlo.
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box width="300px">
                      <FormControl fullWidth variant="filled" color="primary" size="small">
                        <InputLabel id="select-label">Seleziona categoria</InputLabel>
                        <Select
                          labelId="select-label"
                          id="select"
                          label="Seleziona categoria"
                          value={filesOptions[file.name]?.category ?? ''}
                          onChange={(e) =>
                            setFilesOptions(
                              (map): FilesOptions =>
                                e.target.value
                                  ? {
                                      ...map,
                                      [file.name]: { ...(map[file.name] ?? {}), category: e.target.value as string },
                                    }
                                  : map
                            )
                          }
                        >
                          <Box p={1}>
                            <Button
                              fullWidth
                              onClick={() => setOpen(true)}
                              variant="contained"
                              color="primary"
                              size="small"
                              disableElevation
                            >
                              <Add fontSize="small" /> Aggiungi categoria
                            </Button>
                          </Box>
                          {categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Button
                      color="primary"
                      size="small"
                      disableElevation
                      style={{ marginLeft: 5 }}
                      onClick={(e) =>
                        setFilesOptions(
                          (map): FilesOptions => ({
                            ...map,
                            [file.name]: { ...(map[file.name] ?? {}), hidden: !(map[file.name]?.hidden ?? false) },
                          })
                        )
                      }
                    >
                      {filesOptions[file.name]?.hidden ? <VisibilityOff /> : <Visibility />}
                    </Button>
                    <Button
                      color="primary"
                      size="small"
                      disableElevation
                      style={{ marginLeft: 5 }}
                      onClick={() =>
                        setFiles(
                          (files) => (files && Array.from(files)?.filter(({ name }) => name !== file.name)) ?? []
                        )
                      }
                    >
                      <Delete />
                    </Button>
                  </Box>
                ))}
            </Box>
            <Dialog open={open} onClose={() => setOpen(false)} hideBackdrop>
              <Box p={3} width="300px">
                <Typography variant="h6">Aggiungi categoria</Typography>
                <Divider style={{ margin: '10px 0' }} />
                <TextField
                  label="Nome categoria"
                  fullWidth
                  value={categoryLabel}
                  onChange={(e) => setCategoryLabel(e.target.value)}
                />
                <Box height={10} />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={addCategory}
                  disabled={!categoryLabel || categories.some(({ label }) => label === categoryLabel)}
                >
                  Aggiungi
                </Button>
              </Box>
            </Dialog>
          </Box>
        </Paper>
      </Box>
    </PageLayout>
  );
};

export default Upload;
